import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const sizeClasses = {
  sm: 'max-w-sm sm:max-w-sm',
  md: 'max-w-lg sm:max-w-lg',
  lg: 'max-w-2xl sm:max-w-2xl',
  xl: 'max-w-4xl sm:max-w-4xl',
  full: 'max-w-6xl sm:max-w-6xl',
};

export default function Modal({ open, onClose, title, size = 'md', children, footer }: ModalProps) {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        {/* Panel */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-2 py-4 sm:items-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <Dialog.Panel
                className={`flex max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden rounded-card bg-surface-container-lowest shadow-float max-sm:max-w-full sm:max-h-[90vh] ${sizeClasses[size]}`}
              >
                {/* Header */}
                {title && (
                  <div className="flex items-center justify-between px-4 sm:px-card-padding pt-4 sm:pt-card-padding pb-3 sm:pb-4">
                    <Dialog.Title className="text-lg font-semibold tracking-editorial text-on-surface">
                      {title}
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-1 rounded-lg text-on-surface-variant hover:bg-surface-container-low transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {/* Body */}
                <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 sm:px-card-padding sm:pb-card-padding">
                  {children}
                </div>

                {/* Footer */}
                {footer && (
                  <div className="flex items-center justify-end gap-3 px-4 sm:px-card-padding py-3 sm:py-4 bg-surface-container-low">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
